import React, {
  Suspense,
  createContext,
  startTransition,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { LayoutContentFallback } from "./AppBootScreen";
import RouteErrorBoundary from "./RouteErrorBoundary";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import { useBranding, brandLogoSrc } from "../contexts/BrandingContext";
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  ChevronDown,
  ChevronRight,
  LogOut,
  Settings,
  Wallet,
  // Bell,
  FileBarChart,
  Package,
  Menu,
  X,
  Headphones,
  Moon,
  Sun,
  Monitor,
  User,
} from "lucide-react";
import { cn } from "../lib/utils";
import { useCompanyAdminAuth } from "../contexts/CompanyAdminAuthContext";
import {
  clearCompanyAdminViaSuperadminFlag,
  exitDietitianActAsSession,
  isCompanyAdminViaSuperadmin,
} from "../lib/superadminActAs";
import { formatCompanyMoney } from "../lib/money";
import { useCompanyMoney } from "../hooks/useCompanyMoney";
import { ROUTE_ADDON_KEYS, ROUTE_ADDON_ALL_KEYS, listActivatedAddonNav } from "../lib/companyAddons";
import { isHireStaffTicket } from "../lib/ticketUi";
import { companyAdminDisplayBrand } from "../lib/whiteLabeling";
// import { expiredFoodsAPI } from "../lib/companyAdminApi";
import { chatApi } from "../lib/chatApi";
import {
  ticketsAPI,
  supportTicketsAPI,
  companyAdminAppointmentsAPI,
  companyAdminBillingAPI,
  getActAsCompanyToken,
} from "../lib/companyAdminApi";
import { ADMIN_QUERY } from "../lib/adminQuery";
import useStaffUnreadSocket from "../hooks/useStaffUnreadSocket";
import useTicketRealtime, { TICKET_ACTIVITY_EVENT } from "../hooks/useTicketRealtime";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";

const navItems = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/admin",
    permKey: "dashboard",
  },
  {
    icon: MessageSquare,
    label: "Chats",
    path: "/admin/chats",
    permKey: "chats",
  },
  {
    icon: Users,
    label: "Customers",
    path: "/admin/customers",
    permKey: "customers",
  },
];

const financeNavItems = [
  {
    icon: FileBarChart,
    label: "Reports",
    path: "/admin/reports",
    permKey: "reports",
    testId: "nav-reports",
  },
  {
    icon: Wallet,
    label: "Billing",
    path: "/admin/billing",
    permKey: "billing",
  },
  {
    icon: Package,
    label: "Add-ons",
    path: "/admin/addons",
    permKey: "addons",
  },
];

const roleLabel = (userType) => {
  const t = Number(userType);
  if (t === 1) return "Admin";
  if (t === 2) return "Manager";
  if (t === 3) return "Agent";
  return "Staff";
};

const initialsFrom = (name, email) => {
  const source = String(name || email || "U").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

// Bell hidden until a real notification view exists.
// const BellNotification = () => {
//   const [total, setTotal] = React.useState(0);
//   const navigate = useNavigate();
//
//   React.useEffect(() => {
//     expiredFoodsAPI
//       .get()
//       .then((res) => setTotal(res.data.total || 0))
//       .catch(() => {});
//   }, []);
//
//   return (
//     <button
//       onClick={() => navigate("/admin/expired-foods")}
//       className="relative p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
//       data-testid="bell-notification-btn"
//       title="View Expired Food Entries"
//     >
//       <Bell className="w-5 h-5" />
//       {total > 0 && (
//         <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
//           {total > 99 ? "99+" : total}
//         </span>
//       )}
//     </button>
//   );
// };

const CompanyAdminLayoutNest = createContext(false);

function tabNavigate(navigate, to, onAfter) {
  return (event) => {
    onAfter?.();
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    startTransition(() => {
      navigate(to);
    });
  };
}

export default function CompanyAdminLayout({ children }) {
  const nested = useContext(CompanyAdminLayoutNest);
  if (nested) return children ?? null;
  return (
    <CompanyAdminLayoutNest.Provider value={true}>
      <CompanyAdminLayoutShell>
        {children ?? <Outlet />}
      </CompanyAdminLayoutShell>
    </CompanyAdminLayoutNest.Provider>
  );
}

const CompanyAdminLayoutShell = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    company,
    user,
    logout,
    hasPermission,
    userPermissions,
    companyConfig,
    hasAddon,
    token: authToken,
  } = useCompanyAdminAuth();
  const queryClient = useQueryClient();
  const { branding } = useBranding();
  const { theme, setTheme } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const staffToken =
    typeof window !== "undefined"
      ? getActAsCompanyToken() || localStorage.getItem("company_admin_token")
      : null;
  useStaffUnreadSocket({ enabled: !!staffToken, token: staffToken });

  // Permission helpers
  const canSee = (key) => hasPermission(key);
  const canSeeUserManagement =
    (canSee("users") || canSee("groups")) && hasAddon("user_management");
  const canSeePartners =
    (canSee("partners") || canSee("withdrawals")) && hasAddon("partners");
  const canSeeAppointments = hasAddon("booking_appointment");

  // Check if AI Chat is enabled from company config
  // Safe default: hide AI items while config is loading (null) or when explicitly disabled
  const isAiEnabled = companyConfig?.ai_chat === "enabled";

  const activatedAddonNav = useMemo(
    () =>
      listActivatedAddonNav({
        config: companyConfig,
        canSee: (key) => hasPermission(key),
        hasAddon,
        aiEnabled: isAiEnabled,
      }),
    [companyConfig, hasPermission, hasAddon, isAiEnabled],
  );

  // Route guard — map paths to permission keys
  const ROUTE_PERMS = {
    "/admin/chats": "chats",
    "/admin/tickets": "tickets",
    "/admin/ai-chats": "ai_chats",
    "/admin/ai-health": "ai_health",
    "/admin/reports": "reports",
    "/admin/customers": "customers",
    "/admin/addons": "addons",
    "/admin/billing": "billing",
    "/admin/white-labeling": "settings",
    "/admin/app-installations": "customers",
    "/admin/recipes": "customers",
    "/admin/subscriptions": "subscriptions",
    "/admin/payment-history": "payment_history",
    "/admin/payments": "payment_history",
    "/admin/appointments": "customers",
    "/admin/personal-assistant": "addons",
    "/admin/hire-dietitians": "addons",
    "/admin/partners/withdrawals": "withdrawals",
    "/admin/partners": "partners",
    "/admin/settings": "settings",
    "/admin/ai-config": "ai_config",
    "/admin/users": "users",
    "/admin/groups": "groups",
    "/admin/user-management": "users",
  };
  // Find the most specific matching route
  const matchedKey = Object.keys(ROUTE_PERMS)
    .filter(
      (p) => location.pathname === p || location.pathname.startsWith(p + "/"),
    )
    .sort((a, b) => b.length - a.length)[0];
  const addonRequired = matchedKey ? ROUTE_ADDON_KEYS[matchedKey] : null;
  const addonAllRequired = matchedKey
    ? ROUTE_ADDON_ALL_KEYS[matchedKey]
    : null;
  const isUserMgmtRoute =
    location.pathname.startsWith("/admin/user-management") ||
    location.pathname === "/admin/users" ||
    location.pathname === "/admin/groups";
  const isPartnersRoute = location.pathname.startsWith("/admin/partners");
  const currentRouteBlocked =
    (isUserMgmtRoute
      ? !canSeeUserManagement
      : isPartnersRoute
        ? !canSeePartners
        : matchedKey && !canSee(ROUTE_PERMS[matchedKey])) ||
    (addonRequired && !hasAddon(addonRequired)) ||
    (Array.isArray(addonAllRequired) &&
      !addonAllRequired.every((k) => hasAddon(k)));

  const handleLogout = () => {
    if (isCompanyAdminViaSuperadmin()) {
      exitDietitianActAsSession();
      return;
    }
    clearCompanyAdminViaSuperadminFlag();
    logout();
    navigate("/admin/login");
  };

  const { data: chatGroupsData = [] } = useQuery({
    queryKey: ["admin", "chat-groups"],
    queryFn: async () => {
      const res = await chatApi.getGroupsWithUnread();
      return res.data.groups || [];
    },
    enabled: !!staffToken,
    staleTime: 15_000,
    refetchOnMount: true,
    refetchInterval: 30_000,
  });
  const chatGroups = Array.isArray(chatGroupsData) ? chatGroupsData : [];

  const { data: ticketCounts } = useQuery({
    queryKey: ["admin", "ticket-counts"],
    queryFn: async () => {
      const [ticketsRes, supportRes] = await Promise.all([
        hasAddon("tickets")
          ? ticketsAPI.count()
          : Promise.resolve({ data: { count: 0 } }),
        supportTicketsAPI.count(),
      ]);
      return {
        open: ticketsRes.data.count || 0,
        support: supportRes.data.count || 0,
      };
    },
    enabled: !!staffToken,
    staleTime: 120_000,
    refetchOnMount: false,
    refetchInterval: 90_000,
  });
  const openTicketCount = ticketCounts?.open || 0;
  const openSupportCount = ticketCounts?.support || 0;

  const { data: appointmentTodayCount = 0 } = useQuery({
    queryKey: ["admin", "appointment-badge"],
    queryFn: async () => {
      const res = await companyAdminAppointmentsAPI.badge();
      return res.data.today?.total || 0;
    },
    enabled: !!staffToken && canSeeAppointments,
    staleTime: 120_000,
    refetchOnMount: false,
    refetchInterval: 90_000,
  });

  const canSeeBilling = canSee("billing");
  const { currency: moneyCurrency } = useCompanyMoney();
  const { data: billingSummary } = useQuery({
    queryKey: ["admin", "billing", "summary", moneyCurrency],
    queryFn: async () => {
      const res = await companyAdminBillingAPI.summary();
      return {
        balance: Number(res.data.balance) || 0,
        currency: res.data.currency,
        methods: res.data.payment_methods || [],
      };
    },
    ...ADMIN_QUERY,
    enabled: !!staffToken && canSeeBilling,
    refetchInterval: 90_000,
  });
  const headerBalance =
    billingSummary?.balance != null ? Number(billingSummary.balance) || 0 : null;
  const headerBalanceLabel =
    headerBalance == null
      ? "…"
      : formatCompanyMoney(
          headerBalance,
          billingSummary?.currency || moneyCurrency,
        );

  useEffect(() => {
    const onChat = () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "chat-groups"] });
      queryClient.invalidateQueries({ queryKey: ["admin-chat-customers"] });
    };
    const onTickets = (e) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "ticket-counts"] });
      if (isHireStaffTicket(e?.detail?.ticket)) {
        queryClient.invalidateQueries({ queryKey: ["admin", "assigned-staff"] });
      }
    };
    const onAppt = () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "appointment-badge"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "appointments"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "chat-appointments"] });
      queryClient.refetchQueries({
        queryKey: ["admin", "appointments"],
        type: "all",
      });
      queryClient.refetchQueries({
        queryKey: ["admin", "chat-appointments"],
        type: "all",
      });
      queryClient.refetchQueries({
        queryKey: ["admin", "appointment-badge"],
        type: "all",
      });
    };
    window.addEventListener("chat:activity", onChat);
    window.addEventListener(TICKET_ACTIVITY_EVENT, onTickets);
    window.addEventListener("appointment:activity", onAppt);
    return () => {
      window.removeEventListener("chat:activity", onChat);
      window.removeEventListener(TICKET_ACTIVITY_EVENT, onTickets);
      window.removeEventListener("appointment:activity", onAppt);
    };
  }, [queryClient]);

  useTicketRealtime({
    token: staffToken || "",
    enabled: !!staffToken,
    onTicket: (ticket) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "ticket-counts"] });
      if (isHireStaffTicket(ticket)) {
        queryClient.invalidateQueries({ queryKey: ["admin", "assigned-staff"] });
      }
    },
  });

  // Sidebar unread: real-time via socket (chat:unread_updated → chat:activity), 30s fallback poll, visibility refetch, and refetch when navigating to Chats.

  const isChatsActive = location.pathname.startsWith("/admin/chats");
  const isChatGroupSelected =
    isChatsActive && location.search.includes("group_id=");

  const chatTotalUnread = useMemo(() => {
    return (chatGroups || []).reduce(
      (sum, g) => sum + Number(g.unread_count || 0),
      0,
    );
  }, [chatGroups]);

  const displayBrand = companyAdminDisplayBrand(company, branding);
  const logoSrc = displayBrand.logoSrc || brandLogoSrc(branding, { mobile: true });
  const brandName = displayBrand.name;

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, location.search]);

  // Lock body scroll while drawer is open on mobile
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  // Wait for add-on config before sending blocked routes to Dashboard.
  const addonsReady = companyConfig != null || !authToken;
  if (currentRouteBlocked && addonsReady) {
    return <Navigate to="/admin" replace />;
  }

  // Redirect AI routes if AI chat is disabled by SuperAdmin
  const isAiRoute =
    location.pathname.startsWith("/admin/ai-chats") ||
    location.pathname.startsWith("/admin/ai-config");
  if (isAiRoute && addonsReady && !isAiEnabled) {
    return <Navigate to="/admin" replace />;
  }

  const closeMobileNav = () => setMobileNavOpen(false);

  const visibleNav = (item) => {
    if (item.permKey && !canSee(item.permKey)) return false;
    if (item.addonKey && !hasAddon(item.addonKey)) return false;
    return true;
  };

  const renderSimpleNavLink = (item) => {
    const isActive = location.pathname === item.path;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={tabNavigate(navigate, item.path, closeMobileNav)}
        data-testid={item.testId}
        className={cn(
          "nav-item flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors",
          isActive
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
        )}
      >
        <item.icon className="w-5 h-5" strokeWidth={1.5} />
        <span className="text-sm font-semibold flex-1">{item.label}</span>
        {item.path === "/admin/tickets" && openTicketCount > 0 && (
          <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold">
            {openTicketCount}
          </span>
        )}
        {item.path === "/admin/support" && openSupportCount > 0 && (
          <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold">
            {openSupportCount}
          </span>
        )}
      </Link>
    );
  };

  const sidebar = (
    <>
      <div className="h-16 flex items-center justify-between px-4 border-b border-border gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={logoSrc}
            alt={brandName}
            className="h-8 w-8 rounded object-contain"
          />
          <div className="min-w-0">
            <div className="text-sm font-bold text-foreground leading-tight truncate">
              {brandName}
            </div>
            <div className="text-xs text-muted-foreground leading-tight truncate max-w-[160px]">
              {company?.name || company?.email}
            </div>
          </div>
        </div>
        <button
          type="button"
          className="md:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground"
          onClick={closeMobileNav}
          aria-label="Close menu"
          data-testid="mobile-nav-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 py-4 px-3 sidebar overflow-y-auto">
        {navItems.filter(visibleNav).map((item) => {
            const isChats = item.path === "/admin/chats";
            const isActive = location.pathname === item.path;

            if (isChats) {
              const singleGroup =
                chatGroups.length === 1 ? chatGroups[0] : null;
              const chatsActive = location.pathname.startsWith("/admin/chats");

              if (chatGroups.length <= 1) {
                const chatsTo = singleGroup
                  ? `/admin/chats?group_id=${singleGroup.id}`
                  : "/admin/chats";
                return (
                  <Link
                    key={item.path}
                    to={chatsTo}
                    onClick={tabNavigate(navigate, chatsTo, closeMobileNav)}
                    data-testid="nav-chats"
                    className={cn(
                      "nav-item flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors",
                      chatsActive
                        ? "bg-muted text-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <item.icon className="w-5 h-5" strokeWidth={1.5} />
                    <span className="text-sm font-semibold flex-1">Chats</span>
                    {chatTotalUnread ? (
                      <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold">
                        {chatTotalUnread}
                      </span>
                    ) : null}
                  </Link>
                );
              }

              return (
                <div key={item.path} className="mb-1">
                  <div
                    className={cn(
                      "w-full nav-item flex items-center justify-between gap-3 px-3 py-2.5 rounded-md cursor-default",
                      isChatGroupSelected
                        ? "text-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" strokeWidth={1.5} />
                      <span
                        className={cn(
                          "text-sm",
                          isChatGroupSelected ? "font-bold" : "font-semibold",
                        )}
                      >
                        {item.label}
                      </span>
                      {chatTotalUnread ? (
                        <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold">
                          {chatTotalUnread}
                        </span>
                      ) : null}
                    </div>
                    <ChevronDown className="w-4 h-4" />
                  </div>

                  <div className="pl-2 mt-1">
                    {chatGroups.length ? (
                      chatGroups.map((g) => {
                        const active = location.search.includes(
                          `group_id=${g.id}`,
                        );
                        return (
                          <Link
                            key={g.id}
                            to={`/admin/chats?group_id=${g.id}`}
                            onClick={tabNavigate(
                              navigate,
                              `/admin/chats?group_id=${g.id}`,
                              closeMobileNav,
                            )}
                            className={cn(
                              "nav-item flex items-center justify-between gap-3 px-3 py-2 rounded-md mb-1 transition-colors text-sm font-semibold",
                              active
                                ? "bg-muted text-foreground"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground",
                            )}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <ChevronRight
                                className="w-4 h-4"
                                strokeWidth={2}
                              />
                              <span className="truncate">{g.name}</span>
                            </div>
                            {g.unread_count ? (
                              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold">
                                {g.unread_count}
                              </span>
                            ) : null}
                          </Link>
                        );
                      })
                    ) : (
                      <div className="text-xs text-muted-foreground px-3 py-2">
                        No groups assigned
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            return renderSimpleNavLink(item);
          })}

        {financeNavItems.filter(visibleNav).map((item) => {
          if (item.path !== "/admin/addons") {
            return renderSimpleNavLink(item);
          }

          const childIsActive = (child) => {
            if (child.key === "user_management") {
              return (
                location.pathname.startsWith("/admin/user-management") ||
                location.pathname.startsWith("/admin/users") ||
                location.pathname.startsWith("/admin/groups")
              );
            }
            if (child.key === "partners") {
              return location.pathname.startsWith("/admin/partners");
            }
            if (child.key === "payments") {
              return (
                location.pathname.startsWith("/admin/payments") ||
                location.pathname === "/admin/payment-history"
              );
            }
            const [path, query] = String(child.path).split("?");
            if (
              location.pathname !== path &&
              !location.pathname.startsWith(`${path}/`)
            ) {
              return false;
            }
            if (query) return location.search.includes(query);
            return path !== "/admin/addons";
          };
          const childActive = activatedAddonNav.some(childIsActive);
          const parentActive =
            location.pathname === "/admin/addons" && !childActive;

          if (!activatedAddonNav.length) {
            return renderSimpleNavLink(item);
          }

          return (
            <div key={item.path} className="mb-1" data-testid="nav-addons">
              <Link
                to="/admin/addons"
                onClick={tabNavigate(navigate, "/admin/addons", closeMobileNav)}
                className={cn(
                  "nav-item flex items-center justify-between gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors",
                  parentActive
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <span className="flex items-center gap-3 min-w-0">
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="text-sm font-semibold">Add-ons</span>
                </span>
                <ChevronDown className="w-4 h-4 shrink-0" />
              </Link>
              <div className="pl-2">
                {activatedAddonNav.map((child) => {
                  const active = childIsActive(child);
                  const badge =
                    child.key === "tickets"
                      ? openTicketCount
                      : child.key === "booking_appointment"
                        ? appointmentTodayCount
                        : 0;
                  return (
                    <Link
                      key={child.key}
                      to={child.path}
                      onClick={tabNavigate(navigate, child.path, closeMobileNav)}
                      data-testid={`nav-addon-${child.key}`}
                      className={cn(
                        "nav-item flex items-center gap-3 px-3 py-2 rounded-md mb-1 transition-colors text-sm font-semibold",
                        active
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <ChevronRight className="w-4 h-4 shrink-0" strokeWidth={2} />
                      <span className="truncate flex-1">{child.label}</span>
                      {badge > 0 && (
                        <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold">
                          {badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
    </>
  );

  const displayName = user?.name || user?.email || "Account";
  const initials = initialsFrom(user?.name, user?.email);
  const iconBtnClass =
    "relative p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors";

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      {mobileNavOpen && (
        <button
          type="button"
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          aria-label="Close menu overlay"
          onClick={closeMobileNav}
        />
      )}

      <aside
        className={cn(
          "bg-card border-r border-border flex flex-col w-64 shrink-0",
          "fixed md:static inset-y-0 left-0 z-50",
          "transition-transform duration-300 ease-in-out",
          mobileNavOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        )}
        data-testid="company-admin-sidebar"
      >
        {sidebar}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <header className="h-14 shrink-0 border-b border-border bg-card flex items-center justify-between gap-3 px-3 sm:px-4">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-muted text-foreground shrink-0"
              aria-label="Open menu"
              data-testid="mobile-nav-open"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0 hidden sm:block">
              <p className="text-sm font-semibold text-foreground truncate">
                {brandName}
              </p>
              <p className="text-[11px] text-muted-foreground truncate">
                {roleLabel(user?.user_type)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-0.5 sm:gap-1">
            {/* <BellNotification /> */}

            <Link
              to="/admin/support"
              onClick={tabNavigate(navigate, "/admin/support")}
              className={iconBtnClass}
              title="Support"
              aria-label="Support"
            >
              <Headphones className="w-5 h-5" />
              {openSupportCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                  {openSupportCount > 99 ? "99+" : openSupportCount}
                </span>
              )}
            </Link>

            {canSeeBilling && (
              <Link
                to="/admin/billing"
                onClick={tabNavigate(navigate, "/admin/billing")}
                className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                title="Billing"
                aria-label={`Current balance ${headerBalanceLabel}. Open billing`}
                data-testid="admin-header-balance"
              >
                <Wallet className="w-4 h-4 shrink-0" />
                <span className="text-sm font-semibold tabular-nums text-foreground">
                  {headerBalanceLabel}
                </span>
              </Link>
            )}

            {canSee("settings") && (
              <Link
                to="/admin/settings"
                onClick={tabNavigate(navigate, "/admin/settings")}
                className={iconBtnClass}
                title="Settings"
                aria-label="Settings"
              >
                <Settings className="w-5 h-5" />
              </Link>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="ml-1 flex items-center gap-2 rounded-full p-1 pr-1.5 sm:pr-2 hover:bg-muted transition-colors"
                  data-testid="company-admin-user-menu"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:flex flex-col items-start max-w-[140px]">
                    <span className="text-sm font-medium text-foreground truncate w-full text-left">
                      {displayName}
                    </span>
                    <span className="text-[11px] text-muted-foreground truncate w-full text-left">
                      {user?.email}
                    </span>
                  </span>
                  <ChevronDown className="hidden sm:block w-4 h-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{displayName}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {canSee("settings") && (
                  <DropdownMenuItem
                    onClick={() =>
                      startTransition(() => navigate("/admin/settings"))
                    }
                  >
                    <User className="w-4 h-4" />
                    Profile
                  </DropdownMenuItem>
                )}
                {canSee("settings") && (
                  <DropdownMenuItem
                    onClick={() =>
                      startTransition(() => navigate("/admin/settings"))
                    }
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() =>
                    startTransition(() => navigate("/admin/support"))
                  }
                >
                  <Headphones className="w-4 h-4" />
                  Support
                </DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Sun className="w-4 h-4" />
                    Theme
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem onClick={() => setTheme("light")}>
                      <Sun className="w-4 h-4" />
                      Light
                      {theme === "light" ? " ✓" : ""}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTheme("dark")}>
                      <Moon className="w-4 h-4" />
                      Dark
                      {theme === "dark" ? " ✓" : ""}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTheme("system")}>
                      <Monitor className="w-4 h-4" />
                      System
                      {theme === "system" ? " ✓" : ""}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={handleLogout}
                  data-testid="company-admin-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main
          className={cn(
            "flex flex-1 min-h-0 flex-col p-3 sm:p-4 overflow-x-hidden",
            isChatsActive ||
              location.pathname.startsWith("/admin/appointments") ||
              location.pathname.startsWith("/admin/tickets") ||
              location.pathname.startsWith("/admin/support")
              ? "overflow-hidden"
              : "overflow-y-auto",
          )}
        >
          <RouteErrorBoundary key={location.pathname}>
            <Suspense fallback={<LayoutContentFallback />}>
              {children}
            </Suspense>
          </RouteErrorBoundary>
        </main>
      </div>
    </div>
  );
};

